import { error } from "@/services/LoggerService";
import { State } from "@/store";
import Auth from "@aws-amplify/auth";
import { ActionContext, ActionTree } from "vuex";
import { AuthMutations, AuthMutationTypes } from "./mutations";
import { AuthState } from "./state";

export enum AuthActionTypes {
  Login = "LOGIN",
  CompleteNewPassword = "COMPLETE_NEW_PASSWORD",
  Logout = "LOGOUT",
  ForgetPassword = "FORGET_PASSWORD",
  ResetPassword = "RESET_PASSWORD",
  RefreshAuth = "REFRESH_AUTH",
  IsAuthenticated = "IS_AUTHENTICATED",
  GetIdToken = "GET_ID_TOKEN"
}

type AugmentedActionContext = Omit<
  ActionContext<AuthState, State>,
  "commit"
> & {
  commit<K extends keyof AuthMutations>(
    key: K,
    payload: Parameters<AuthMutations[K]>[1]
  ): ReturnType<AuthMutations[K]>;
};

type AuthLoginParameter = {
  email: string;
  password: string;
};

type ResetPasswordParameters = {
  email: string;
  code: string;
  password: string;
};

type CognitoUserObject = {
  attributes: {
    email: string;
    name: string;
    sub: string;
  };
  signInUserSession: {
    idToken: CognitoToken;
    accessToken: CognitoToken;
    refreshToken: CognitoRefreshToken;
  };
  challengeName: string;
  storage?: {
    vuex?: string;
  };
  pool?: {
    storage: {
      vuex?: string;
    };
  };
};

type CognitoToken = {
  jwtToken: string;
  payload: {
    "cognito:groups": Array<string>;
    exp: number;
    iat: number;
  };
};

type CognitoRefreshToken = {
  token: string;
};

export type AuthActions = {
  [AuthActionTypes.Login](
    { commit }: AugmentedActionContext,
    credentials: AuthLoginParameter
  ): Promise<CognitoUserObject>;
  [AuthActionTypes.RefreshAuth](
    { commit }: AugmentedActionContext
  ): Promise<boolean>;
  [AuthActionTypes.CompleteNewPassword](
    { getters }: AugmentedActionContext,
    newPassword: string
  ): Promise<boolean>;
  [AuthActionTypes.Logout]({ commit }: AugmentedActionContext): Promise<void>;
  [AuthActionTypes.ForgetPassword](
    { commit }: AugmentedActionContext,
    email: string
  ): Promise<void>;
  [AuthActionTypes.ResetPassword](
    { commit }: AugmentedActionContext,
    resetPasswordParams: ResetPasswordParameters
  ): Promise<void>;
  [AuthActionTypes.IsAuthenticated](
    { getters, dispatch }: AugmentedActionContext
  ): Promise<boolean>;
  [AuthActionTypes.GetIdToken](
    { getters, dispatch }: AugmentedActionContext
  ): Promise<string>;
};



async function handleAuthResult(commit: <K extends keyof AuthMutations>(key: K, payload: Parameters<AuthMutations[K]>[1]) => ReturnType<AuthMutations[K]>, result: CognitoUserObject): Promise<CognitoUserObject> {
  if (!result) {
    throw new Error("Invalid user object");
  }

  if (result?.storage?.vuex) {
    result.storage.vuex = undefined;
  }

  if (result?.pool?.storage?.vuex) {
    result.pool.storage.vuex = undefined;
  }
  const idToken = result.signInUserSession?.idToken.jwtToken;
  const accessToken = result.signInUserSession?.accessToken.jwtToken;
  const refreshToken = result.signInUserSession?.refreshToken.token;
  const exp = idToken ? (await Auth.currentSession()).getIdToken().getExpiration() : undefined;
  const name = result?.attributes?.name;
  const groups =
    result.signInUserSession?.accessToken.payload["cognito:groups"];

  commit(AuthMutationTypes.SetUser, result);
  commit(AuthMutationTypes.SetTokens, {
    idToken,
    accessToken,
    refreshToken,
    exp
  });
  commit(AuthMutationTypes.SetName, name);
  commit(AuthMutationTypes.SetGroups, groups);
  return result;
}

export const actions: ActionTree<AuthState, State> & AuthActions = {
  async [AuthActionTypes.Login]({ commit }, credentials) {
    try {
      const email = credentials.email.toLocaleLowerCase();
      const result: CognitoUserObject = await Auth.signIn(
        email,
        credentials.password
      );

      return await handleAuthResult(commit, result);
    } catch (e) {
      error(e);
      throw e;
    }
  },
  async [AuthActionTypes.RefreshAuth]({ commit }) {
    try {
      const result: CognitoUserObject = await Auth.currentAuthenticatedUser();
      await handleAuthResult(commit, result);
      return true;
    } catch (e) {
      error(e);
      throw e;
    }
  },
  async [AuthActionTypes.CompleteNewPassword]({ getters }, newPassword) {
    try {
      const user = <CognitoUserObject>getters.getUser;
      if (!user) {
        throw new Error("User object null");
      }

      if (user.challengeName != "NEW_PASSWORD_REQUIRED") {
        throw new Error("User challenge is not set to new password required");
      }

      await Auth.completeNewPassword(user, newPassword);
      return true;
    } catch (e) {
      error(e);
      error("Failed complete new password flow");
      throw e;
    }
  },
  async [AuthActionTypes.Logout]({ commit }) {
    await Auth.signOut();
    commit(AuthMutationTypes.PurgeAuth, undefined);
  },
  async [AuthActionTypes.ForgetPassword]({ commit }, email) {
    try {
      const emailLowerCase = email.toLocaleLowerCase();
      await Auth.forgotPassword(emailLowerCase);
    } catch (e) {
      error(e);
      error("Failed to forget the password");
      throw e;
    }
  },
  async [AuthActionTypes.ResetPassword]({ commit }, params) {
    try {
      const email = params.email.toLocaleLowerCase();

      await Auth.forgotPasswordSubmit(
        email,
        params.code,
        params.password
      );
    } catch (e) {
      error(e);
      error("Failed to reset the password");
      throw e;
    }
  },
  async [AuthActionTypes.IsAuthenticated]({ getters, dispatch }) {
    try {
      if (!getters.isAuthenticated) {
        return false;
      }

      if (!getters.isExpired) {
        return true;
      }

      return await dispatch(AuthActionTypes.RefreshAuth);
    } catch (e) {
      error(e);
      error("Failed to reset the password");
    }
    return false;

  },
  async [AuthActionTypes.GetIdToken]({ getters, dispatch }) {
    try {
      if (!getters.isAuthenticated) {
        return undefined;
      }

      if (!getters.isExpired) {
        return getters.getIdToken;
      }

      await dispatch(AuthActionTypes.RefreshAuth);
      return getters.getIdToken;
    } catch (e) {
      error(e);
      error("Failed to reset the password");
      throw e;
    }
  },
};


